import { Controller } from "@hotwired/stimulus"
import { showHubTeamToast } from "../utils/hub_team_toast"

export default class extends Controller {
  static targets = [
    "panel",
    "form",
    "firstNameInput",
    "lastNameInput",
    "emailInput",
    "roleInput",
    "accessLevelInput",
    "submitButton",
    "resendEmailButton",
    "resendEmailLabel",
    "errorAlert",
    "errorMessage"
  ]

  static values = {
    resendUrl: String
  }

  connect() {
    this.companyUserId = null
    this.originalValues = {}
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.unlockBodyScroll()
  }

  open(event) {
    event.preventDefault()

    const row = event.currentTarget.closest("tr")
    if (!row) return

    this.companyUserId = row.dataset.companyUserId
    this.updateUrl = row.dataset.updateUrl
    this.sendInvitationUrl = row.dataset.invited === "true" ? null : row.dataset.sendInvitationUrl
    this.updateInvitationButton()
    this.populateForm(row.dataset)
    this.clearError()
    this.showPanel()
    this.validate()
  }

  close(event) {
    event?.preventDefault()

    if (!this.hasPanelTarget || this.panelTarget.hidden) return

    this.hidePanel()
    this.companyUserId = null
    this.updateUrl = null
    this.sendInvitationUrl = null
    this.clearError()
  }

  validate() {
    if (!this.hasSubmitButtonTarget) return

    const saving = this.submitButtonTarget.dataset.saving === "true"
    this.submitButtonTarget.disabled = saving || !this.formValid() || !this.formChanged()
  }

  async save(event) {
    event.preventDefault()

    if (!this.updateUrl || !this.formValid() || !this.formChanged()) return

    this.setSaving(true)
    this.clearError()

    const formData = new FormData()
    formData.append("_method", "PATCH")
    formData.append("company_member[first_name]", this.firstNameInputTarget.value.trim())
    formData.append("company_member[last_name]", this.lastNameInputTarget.value.trim())
    formData.append("company_member[email]", this.emailInputTarget.value.trim())
    formData.append("company_member[role]", this.selectedRole())
    formData.append("company_member[access_level]", this.selectedAccessLevel())

    try {
      const response = await fetch(this.updateUrl, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.updateRow(data)
        this.close()
      } else {
        const message = data?.errors?.join(", ") || `HTTP ${response.status}`
        this.showError(message)
      }
    } catch (error) {
      console.error("Error updating company member:", error)
      this.showError("An error occurred. Please try again.")
    } finally {
      this.setSaving(false)
      this.validate()
    }
  }

  // Same button, two jobs: a staged member gets their first invitation, an already-invited one
  // gets a fresh copy of the one they were sent.
  async resendEmail(event) {
    event.preventDefault()

    if (!this.hasResendEmailButtonTarget) return

    const sendingFirstInvitation = Boolean(this.sendInvitationUrl)
    const url = sendingFirstInvitation ? this.sendInvitationUrl : this.resendUrlValue
    if (!url || (!sendingFirstInvitation && (!this.companyUserId || !this.hasResendUrlValue))) return

    this.resendEmailButtonTarget.disabled = true

    const formData = new FormData()
    if (!sendingFirstInvitation) formData.append("company_user_ids[]", this.companyUserId)

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (response.ok && data.success) {
        if (sendingFirstInvitation) {
          window.location.reload()
          return
        }
      } else {
        const message = data?.errors?.join(", ") || `HTTP ${response.status}`
        showHubTeamToast(`Failed to send invitation: ${message}`, { variant: "error" })
      }
    } catch (error) {
      console.error("Error sending invitation:", error)
      showHubTeamToast("An error occurred. Please try again.", { variant: "error" })
    }

    this.resendEmailButtonTarget.disabled = false
  }

  updateInvitationButton() {
    if (!this.hasResendEmailLabelTarget) return

    this.resendEmailLabelTarget.textContent = this.sendInvitationUrl ? "Send Invitation" : "Resend Email"
  }

  populateForm(dataset) {
    this.firstNameInputTarget.value = dataset.firstName || ""
    this.lastNameInputTarget.value = dataset.lastName || ""
    this.emailInputTarget.value = dataset.email || ""

    const role = dataset.memberRole || ""
    this.roleInputTargets.forEach((input) => {
      input.checked = input.value === role
    })

    const accessLevel = dataset.accessLevel || ""
    this.accessLevelInputTargets.forEach((input) => {
      input.checked = input.value === accessLevel
    })

    this.originalValues = this.currentValues()
  }

  currentValues() {
    return {
      firstName: this.firstNameInputTarget.value.trim(),
      lastName: this.lastNameInputTarget.value.trim(),
      email: this.emailInputTarget.value.trim(),
      role: this.selectedRole(),
      accessLevel: this.selectedAccessLevel()
    }
  }

  formValid() {
    const values = this.currentValues()

    return values.firstName !== "" &&
      values.lastName !== "" &&
      values.email !== "" &&
      values.role !== "" &&
      values.accessLevel !== ""
  }

  formChanged() {
    const values = this.currentValues()

    return values.firstName !== this.originalValues.firstName ||
      values.lastName !== this.originalValues.lastName ||
      values.email !== this.originalValues.email ||
      values.role !== this.originalValues.role ||
      values.accessLevel !== this.originalValues.accessLevel
  }

  selectedRole() {
    const selected = this.roleInputTargets.find((input) => input.checked)
    return selected?.value || ""
  }

  selectedAccessLevel() {
    const selected = this.accessLevelInputTargets.find((input) => input.checked)
    return selected?.value || ""
  }

  updateRow(data) {
    const row = this.element.querySelector(`tr[data-company-user-id="${data.id}"]`)
    if (!row) return

    row.dataset.firstName = data.first_name || ""
    row.dataset.lastName = data.last_name || ""
    row.dataset.email = data.email || ""
    row.dataset.memberRole = data.role || ""
    row.dataset.accessLevel = data.access_level || ""

    const nameCell = row.querySelector('[data-sort-key="name"]')
    if (nameCell) {
      nameCell.dataset.sortValue = data.full_name || ""
      const filterEmail = data.email || ""
      nameCell.dataset.filterValue = [data.full_name, filterEmail].filter(Boolean).join(" ").toLowerCase()

      const nameText = nameCell.querySelector(".hub-team-member-name-text")
      if (nameText) nameText.textContent = data.full_name || "N/A"

      const avatar = nameCell.querySelector(".hub-team-member-avatar")
      if (avatar) avatar.textContent = this.initials(data.full_name)
    }

    const emailCell = row.querySelector('[data-sort-key="email"]')
    if (emailCell) {
      emailCell.dataset.sortValue = data.email || ""
      emailCell.dataset.filterValue = (data.email || "").toLowerCase()
      emailCell.textContent = data.email || ""
    }

    this.updateSelectField(row, "role", data.role, data.role_label)
    this.updateSelectField(row, "access_level", data.access_level, data.access_level_label)
  }

  updateSelectField(row, field, value, label) {
    const cell = row.querySelector(`[data-sort-key="${field}"]`)
    const fieldElement = cell?.querySelector('[data-controller~="company-user-field"]')
    if (!cell || !fieldElement) return

    cell.dataset.sortValue = value
    cell.dataset.filterValue = value.toLowerCase()

    const hidden = fieldElement.querySelector(".select-hidden-input-value")
    const labelElement = fieldElement.querySelector("[data-app-select-target='label']")
    const options = fieldElement.querySelectorAll("[data-app-select-target='option']")

    if (hidden) hidden.value = value
    if (labelElement) labelElement.textContent = label

    options.forEach((option) => {
      option.classList.toggle("app-select-option-selected", option.dataset.value === value)
    })

    const fieldController = this.application.getControllerForElementAndIdentifier(fieldElement, "company-user-field")
    if (fieldController) fieldController.previousValue = value
  }

  initials(fullName) {
    if (!fullName) return ""

    return fullName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase()
  }

  showPanel() {
    this.panelTarget.hidden = false
    this.panelTarget.setAttribute("aria-hidden", "false")
    this.lockBodyScroll()
    this.firstNameInputTarget.focus()
  }

  hidePanel() {
    this.panelTarget.hidden = true
    this.panelTarget.setAttribute("aria-hidden", "true")
    this.unlockBodyScroll()
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.hasPanelTarget && !this.panelTarget.hidden) {
      this.close()
    }
  }

  lockBodyScroll() {
    document.body.classList.add("edit-member-panel-open")
  }

  unlockBodyScroll() {
    document.body.classList.remove("edit-member-panel-open")
  }

  setSaving(saving) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.dataset.saving = saving ? "true" : "false"
    this.submitButtonTarget.textContent = saving ? "Saving..." : "Save Changes"
  }

  showError(message) {
    if (!this.hasErrorAlertTarget || !this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = message
    this.errorAlertTarget.classList.remove("d-none")
  }

  clearError() {
    if (!this.hasErrorAlertTarget || !this.hasErrorMessageTarget) return

    this.errorMessageTarget.textContent = ""
    this.errorAlertTarget.classList.add("d-none")
  }
}
